import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";
import { COMPANY, whatsappHref } from "@/lib/company";
import { cn } from "@/lib/utils";

const HIDDEN_ROUTES = ["/brochure", "/admin"];

export const WhatsAppButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, []);

  const hidden = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));
  if (hidden) return null;

  return (
    <>
      {/* Popover */}
      {open && (
        <div
          className="fixed bottom-24 right-4 z-[60] w-72 bg-background border border-border shadow-xl rounded-sm overflow-hidden fade-in-up"
          data-testid="whatsapp-popover"
        >
          <div className="bg-[#075e54] text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Campusco</p>
              <p className="text-[11px] text-white/75">Typically replies within business hours</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-white/10 rounded"
              aria-label="Close chat"
              data-testid="whatsapp-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 bg-[#e5ddd5]">
            <div className="bg-white rounded-lg p-3 text-xs text-foreground max-w-[220px] shadow-sm">
              Hi there 👋 <br />
              How can we help you find your next student residence — or discuss managing yours?
            </div>
          </div>
          <a
            href={whatsappHref()}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-[#25d366] hover:bg-[#1fbf59] text-white text-center py-3 font-semibold text-sm transition-colors"
            data-testid="whatsapp-open-chat"
          >
            Start Chat on WhatsApp
          </a>
          <p className="px-4 py-2 text-[10px] text-muted-foreground text-center">
            {COMPANY.phoneDisplay}
          </p>
        </div>
      )}

      {/* Floating action button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-fab"
        className={cn(
          "no-print fixed bottom-4 right-4 z-[60] group flex items-center gap-3 pl-4 pr-5 py-3.5 rounded-full",
          "bg-[#25d366] hover:bg-[#1fbf59] text-white shadow-lg shadow-[#25d366]/30 transition-all",
          "hover:-translate-y-0.5 hover:shadow-xl",
          ready ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        )}
        style={{ marginBottom: "56px" /* keep clear of Emergent badge */ }}
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.52 3.48A11.94 11.94 0 0 0 12.03 0C5.46 0 .13 5.33.13 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.32-1.66a11.9 11.9 0 0 0 5.7 1.45h.01c6.57 0 11.9-5.33 11.9-11.9 0-3.18-1.24-6.17-3.41-8.4ZM12.03 21.5h-.01a9.6 9.6 0 0 1-4.9-1.34l-.35-.21-3.75.99 1-3.65-.23-.37a9.6 9.6 0 0 1-1.47-5.11c0-5.31 4.32-9.63 9.63-9.63a9.57 9.57 0 0 1 6.81 2.82 9.57 9.57 0 0 1 2.82 6.81c0 5.31-4.32 9.69-9.63 9.69Zm5.29-7.22c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.19.29-.75.95-.92 1.14-.17.19-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.44-1.72-1.6-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.66-1.6-.91-2.19-.24-.57-.48-.5-.66-.51-.17-.01-.36-.01-.55-.01a1.06 1.06 0 0 0-.77.36c-.27.29-1.02 1-1.02 2.44s1.05 2.83 1.19 3.03c.15.19 2.06 3.14 4.99 4.4.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.11.57-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.19-.56-.34Z"/>
        </svg>
        <span className="hidden sm:inline text-sm font-semibold">WhatsApp</span>
      </button>
    </>
  );
};
